import axios from 'axios';
import {
  InitAuthRequest,
  InitAuthResponse,
  VerifyLoginSmsOtpRequest,
  VerifyUserRegisterSmsOtpRequest,
  VerifyHostRegisterSmsOtpRequest,
  VerifyTutorRegisterSmsOtpRequest,
  VerifyHostJoinSmsOtpRequest,
  VerifySmsOtpResponse,
  CompleteUserProfileRequest,
  CompleteHostProfileRequest,
  CompleteTutorSimpleProfileRequest,
  TokenRefreshRequest,
  TokenRefreshResponse,
  TokenIntrospectRequest,
  TokenIntrospectResponse
} from './models/Auth';
import { extractErrorMessage } from './utils/errorHandler';
import { applyClientChannelHeader } from './utils/clientChannel';

// Define the base auth API URL based on the environment
const baseAuthURL = process.env.REACT_APP_API_BASE_URL + "/public/auth";

// Create a class to encapsulate the auth API client logic
class AuthApiClient {
  private authAxiosInstance;

  constructor() {
    this.authAxiosInstance = axios.create({
      baseURL: baseAuthURL,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.authAxiosInstance.interceptors.request.use(
      (config) => {
        applyClientChannelHeader(config.headers);
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  public async initiateLogin(request: InitAuthRequest): Promise<InitAuthResponse> {
    try {
      const response = await this.authAxiosInstance.post<InitAuthResponse>('/initiate', request);
      return response.data;
    } catch (error: any) {
      console.error('Error initiating login:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async verifyLoginOtp(request: VerifyLoginSmsOtpRequest): Promise<VerifySmsOtpResponse> {
    try {
      const response = await this.authAxiosInstance.post<VerifySmsOtpResponse>('/verify', request);
      return response.data;
    } catch (error) {
      console.error('Error verifying login OTP:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async initiateRegister(request: InitAuthRequest): Promise<InitAuthResponse> {
    try {
      const response = await this.authAxiosInstance.post<InitAuthResponse>('/register/initiate', request);
      return response.data;
    } catch (error) {
      console.error('Error initiating registration:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async verifyUserRegisterOtp(request: VerifyUserRegisterSmsOtpRequest): Promise<VerifySmsOtpResponse> {
    try {
      const response = await this.authAxiosInstance.post<VerifySmsOtpResponse>('/register/user/verify-otp', request);
      return response.data;
    } catch (error) {
      console.error('Error verifying user registration OTP:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async verifyHostRegisterOtp(request: VerifyHostRegisterSmsOtpRequest): Promise<VerifySmsOtpResponse> {
    try {
      const response = await this.authAxiosInstance.post<VerifySmsOtpResponse>('/register/host/verify-otp', request);
      return response.data;
    } catch (error) {
      console.error('Error verifying host registration OTP:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async verifyTutorRegisterOtp(request: VerifyTutorRegisterSmsOtpRequest): Promise<VerifySmsOtpResponse> {
    try {
      const response = await this.authAxiosInstance.post<VerifySmsOtpResponse>('/register/tutor/verify-otp', request);
      return response.data;
    } catch (error) {
      console.error('Error verifying tutor registration OTP:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async verifyHostJoinOtp(request: VerifyHostJoinSmsOtpRequest): Promise<VerifySmsOtpResponse> {
    try {
      const response = await this.authAxiosInstance.post<VerifySmsOtpResponse>('/register/host/join/verify-otp', request);
      return response.data;
    } catch (error) {
      console.error('Error verifying host join OTP:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async completeUserProfile(request: CompleteUserProfileRequest): Promise<void> {
    await this.authAxiosInstance.post('/user/profile/complete', request);
  }

  public async completeHostProfile(request: CompleteHostProfileRequest): Promise<void> {
    await this.authAxiosInstance.post('/host/profile/complete', request);
  }

  public async completeTutorProfile(request: CompleteTutorSimpleProfileRequest): Promise<void> {
    await this.authAxiosInstance.post('/tutor/profile/complete', request);
  }

  public async refreshToken(request: TokenRefreshRequest): Promise<TokenRefreshResponse> {
    try {
      const response = await this.authAxiosInstance.post<TokenRefreshResponse>('/refresh-token', request);
      return response.data;
    } catch (error) {
      console.error('Error refreshing token:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  public async introspectToken(request: TokenIntrospectRequest): Promise<TokenIntrospectResponse> {
    try {
      const response = await this.authAxiosInstance.post<TokenIntrospectResponse>('/introspect-token', request);
      return response.data;
    } catch (error) {
      console.error('Error introspecting token:', error);
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }
}

export const authApiClient = new AuthApiClient(); 